import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
/**
 * Super Admin → Online stores (B-84): every business that has an online store, its address and recent storefront
 * orders, and a takedown (abuse, phishing) that closes the shop until the platform restores it. Audited with a reason.
 */
const tag = { tag: "platform-admin" };
export const platformStorefrontSchema = z.object({
    tenantId: z.uuid(),
    businessName: z.string(),
    shopUrl: z.string(),
    enabled: z.boolean(),
    enabledAt: z.iso.datetime().nullable(),
    takenDownAt: z.iso.datetime().nullable(),
    takenDownReason: z.string().nullable(),
    /** Online-store orders in the last 30 days. */
    orders30d: z.int(),
});
export const listPlatformStorefronts = defineEndpoint({
    ...tag,
    name: "listPlatformStorefronts",
    method: "GET",
    path: "/v1/platform/storefronts",
    access: { platformAdmin: true },
    summary: "Businesses with an online store, newest first",
    status: 200,
    response: z.object({ data: z.array(platformStorefrontSchema) }),
});
export const storefrontActionSchema = z.object({ reason: z.string().trim().min(3).max(500) }).strict();
export const takeDownStorefront = defineEndpoint({
    ...tag,
    name: "takeDownStorefront",
    method: "POST",
    path: "/v1/platform/storefronts/{tenantId}/takedown",
    access: { platformAdmin: true },
    summary: "Close a shop for abuse; the merchant cannot reopen it",
    params: z.object({ tenantId: z.uuid() }),
    body: storefrontActionSchema,
    status: 200,
    response: platformStorefrontSchema,
});
export const restoreStorefront = defineEndpoint({
    ...tag,
    name: "restoreStorefront",
    method: "POST",
    path: "/v1/platform/storefronts/{tenantId}/restore",
    access: { platformAdmin: true },
    summary: "Reopen a shop that was taken down",
    params: z.object({ tenantId: z.uuid() }),
    body: storefrontActionSchema,
    status: 200,
    response: platformStorefrontSchema,
});
/** ST-25 (docs/specs/theme-demos.md): one theme's demo shop in production. */
export const THEME_DEMO_STATUSES = ["missing", "ready", "closed", "taken"];
export const themeDemoSchema = z.object({
    theme: z.string(),
    slug: z.string(),
    /** `taken`: a business that is not a demo shop has this address (never built over). */
    status: z.enum(THEME_DEMO_STATUSES),
    businessName: z.string().nullable(),
    shopUrl: z.string().nullable(),
});
export const listThemeDemos = defineEndpoint({
    ...tag,
    name: "listThemeDemos",
    method: "GET",
    path: "/v1/platform/theme-demos",
    access: { platformAdmin: true },
    summary: "The demo shop of every storefront theme (the Admin's Live demo links) and whether a build is running",
    status: 200,
    response: z.object({ data: z.array(themeDemoSchema), building: z.boolean() }),
});
export const buildThemeDemos = defineEndpoint({
    ...tag,
    name: "buildThemeDemos",
    method: "POST",
    path: "/v1/platform/theme-demos/build",
    access: { platformAdmin: true },
    summary: "Build the missing theme demo shops in the background",
    status: 202,
    response: z.object({ queued: z.boolean() }),
});
//# sourceMappingURL=storefront-platform.js.map